/* eslint-disable max-lines */
import { Form, Select, Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { PlusButton } from 'src/components/common/PlusButton/PlusButton';
import { DateRangePicker } from 'src/components/DateRangePicker/DateRangePicker';
import { SearchPanel } from 'src/components/SearchPanel/SearchPanel';
import {
    SearchPanelFilterForm,
    SearchPanelFilterFormProps,
} from 'src/components/SearchPanel/SearchPanelFilterForm';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import { getAgentApplicationStatuses } from 'src/pages/AgentLoanApplicationsPage/AgentApplicationStatusInfoMap';
import { AgentLoanApplicationsStore } from 'src/stores/AgentLoanApplicationsStore/AgentLoanApplicationsStore';
import { UserStore } from 'src/stores/UserStore/UserStore';
import styled from 'styled-components';

const store = AgentLoanApplicationsStore;

export const AgentLoanApplicationsHeaderPanel = observer(() => {
    const { t } = useTranslation();

    return (
        <StyledHeaderWrapper>
            <SearchPanel
                store={store}
                renderPanel={(props) => <FilterPanel {...props} />}
                inputSearchPlaceholder={t(
                    'Page.Agent.Applications.Filter.Search.Placeholder',
                )}
            />
            {UserStore.can.manage.agentLoanApplications && (
                <Tooltip
                    title={t(
                        'Page.AgentLoanApplication.AsideHeader.CreateNewItem.Tooltip',
                    )}
                >
                    <PlusButton
                        onClick={() => {
                            const createLink =
                                EntityLinks.agentLoanApplications.process?.(
                                    createApplicationProcessName,
                                );
                            createLink && Router.navigate(createLink);
                        }}
                        data-testid="header-panel-create-btn"
                    />
                </Tooltip>
            )}
        </StyledHeaderWrapper>
    );
});

const FilterPanel = observer((props: SearchPanelFilterFormProps) => {
    const statuses = getAgentApplicationStatuses();
    const { t } = useTranslation();

    return (
        <SearchPanelFilterForm
            {...props}
            initialValues={{ issuedAt: [] }}
            onFinish={(values) => {
                const { issuedAt, ...other } = values;
                store.pager.reset();
                const [issueDateFrom, issueDateTo] = issuedAt || [
                    undefined,
                    undefined,
                ];
                issueDateFrom?.set('hour', 0).set('minute', 0).set('second', 0);
                issueDateTo
                    ?.set('hour', 23)
                    .set('minute', 59)
                    .set('second', 59);
                store.filterCriteria?.applyFilter({
                    ...other,
                    issueDateFrom,
                    issueDateTo,
                });
                store.loadList();
            }}
        >
            <StyledSelect
                label={t('Page.AgentLoanApplication.Filter.Status')}
                name="status"
            >
                <Select mode="multiple" allowClear data-testid="status">
                    {statuses.map((item) => (
                        <Select.Option
                            value={item.id}
                            key={item.id}
                            data-testid={item.id}
                        >
                            {t(`data:status.application.${item.id}`)}
                        </Select.Option>
                    ))}
                </Select>
            </StyledSelect>
            <Form.Item
                label={t('Page.AgentLoanApplication.Filter.IssuedAt')}
                name="issuedAt"
            >
                <DateRangePicker data-testid="issuedAt" />
            </Form.Item>
        </SearchPanelFilterForm>
    );
});

const createApplicationProcessName = 'SendApplicationFromAgentPortalProcess';

const StyledHeaderWrapper = styled.header`
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto;
`;

const StyledSelect = styled(Form.Item)`
    .ant-select:not(.ant-select-customize-input) .ant-select-selector {
        display: flex;
        align-items: center;
        min-height: 2.7rem;
        border-radius: 0.5rem;
    }
`;
